import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Navigation,
  Pagination,
  Thumbs,
  Keyboard,
  FreeMode,
} from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Modal, Spinner, Image } from '../../../../../components';
import {
  PresentationMetadata,
  SlideContent,
  SlideImage,
  EmptyState,
} from './components';
import type { ViewPresentationModalProps } from './types';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import './styles.css';

export const ViewPresentationModal: React.FC<ViewPresentationModalProps> = ({
  open,
  onClose,
  presentation,
  loading,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveSlideIndex(0);
    } else {
      setThumbsSwiper(null);
    }
  }, [open, presentation?.id]);

  const hasSlides = presentation?.slides && presentation.slides.length > 0;
  const currentSlide = presentation?.slides?.[activeSlideIndex];

  if (loading) {
    return (
      <Modal
        size="medium"
        open={open}
        onClose={onClose}
        title="Loading Presentation..."
      >
        <div className="flex justify-center items-center py-12">
          <Spinner className="h-16 w-16" />
        </div>
      </Modal>
    );
  }

  if (!presentation) {
    return (
      <Modal size="big" open={open} onClose={onClose} title="Presentation">
        <EmptyState message="No presentation data available" />
      </Modal>
    );
  }

  const showThumbnails = presentation.slides && presentation.slides.length > 1;

  return (
    <Modal
      size="big"
      open={open}
      onClose={onClose}
      title={presentation.title || 'Presentation Details'}
    >
      <div className="presentation-modal-wrapper">
        <div
          className={`presentation-modal-content space-y-6 ${
            showThumbnails ? 'has-thumbnails' : ''
          }`}
        >
          {hasSlides && (
            <div className="space-y-4">
              <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                <Swiper
                  modules={[Navigation, Pagination, Thumbs, Keyboard]}
                  navigation
                  pagination={{
                    type: 'fraction',
                    formatFractionCurrent: number => number,
                    formatFractionTotal: number => number,
                  }}
                  thumbs={{
                    swiper:
                      thumbsSwiper && !thumbsSwiper.destroyed
                        ? thumbsSwiper
                        : null,
                  }}
                  keyboard={{ enabled: true }}
                  onSlideChange={swiper =>
                    setActiveSlideIndex(swiper.activeIndex)
                  }
                  className="w-full"
                  style={{ minHeight: '400px' }}
                >
                  {presentation.slides?.map((slide, index) => (
                    <SwiperSlide key={index}>
                      <div className="flex items-center justify-center bg-gray-100 p-4">
                        <SlideImage
                          imageUrl={slide.imageUrl}
                          altText={`Slide ${index + 1}`}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <PresentationMetadata presentation={presentation} />

              {currentSlide && <SlideContent slide={currentSlide} />}
            </div>
          )}
        </div>

        {showThumbnails && (
          <div className="fixed-thumbnails-section">
            <div className="space-y-2 pt-3 pb-2 px-1">
              <span className="text-sm font-medium text-gray-500">
                Jump to Slide:
              </span>
              <Swiper
                modules={[Thumbs, FreeMode, Navigation]}
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView="auto"
                freeMode={true}
                watchSlidesProgress
                navigation={false}
                className="w-full thumbnails-swiper"
              >
                {presentation.slides?.map((slide, index) => (
                  <SwiperSlide
                    key={index}
                    className="!w-[120px] cursor-pointer"
                  >
                    <div
                      className={`relative border-2 rounded-lg overflow-hidden transition-all ${
                        activeSlideIndex === index
                          ? 'border-main border-2'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ height: '80px' }}
                    >
                      <Image
                        src={slide.imageUrl}
                        alt={`Slide ${index + 1}`}
                        wrapperClassName="w-full h-full"
                        imageClassName="w-full h-full object-cover"
                        showLoadingIndicator={false}
                        fallbackComponent={
                          <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400 text-xs">
                            No image
                          </div>
                        }
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs text-center py-1">
                        {index + 1}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
